import {
    Address, BooleanEnum, Borrower, Employment, LoanDetail, OCRFieldAction, amortizationTypeDisplay,
    automatedUwRecommendationDisplay, automatedUwSystemDisplay, booleanEnumDisplay, commitmentTypeDisplay,
    documentationTypeDisplay, loanPurposeDisplay, loanTypeDisplay, numUnitsDisplay, occupancyTypeDisplay,
    ocrFieldActionDisplay, propertyTypeDisplay, specialtyProgramDisplay
} from '@api';
import {
    CheckCircleOutline, Clear, EditNote, HourglassBottom, Person, PersonAdd
} from '@mui/icons-material';
import { Divider, Tooltip, Typography } from '@mui/material';
import {
    AddressFieldset, CardTable, CurrencyField, DateField,
    LabelGroup, LabeledValue, PercentField, TextField, renderEnumOptions
} from '@tsp-ui/core/components';
import { formatCurrency, formatDate } from '@tsp-ui/core/utils';
import clsx from 'clsx';
import { Fragment, ReactNode } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';


import { BorrowerInfoTooltip } from './BorrowerInfoTooltip';
import ocrDialogStyles from './OCRReviewDialog.module.scss';
import styles from './OCRReviewForm.module.scss';
import { SSNDisplay } from './SSNDisplay';


export type OCRFieldValues = {
    action: OCRFieldAction;
    overwriteValue?: string | number | boolean | Partial<Address>;
}

export type BorrowerOCRFormValues = { [P in keyof Omit<Borrower, 'id'>]?: OCRFieldValues } & {
    isNewBorrower?: boolean; // for frontend form use only
    id: string; // for frontend form use only
}

export type OCRDataReviewFormValues = {
  [P in keyof Omit<LoanDetail, 'borrowers'>]?: OCRFieldValues
} & {
    borrowers?: BorrowerOCRFormValues[];
};

interface OCRReviewFormProps {
    original?: LoanDetail;
    updated?: Partial<LoanDetail>;
    handleSubmit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
}

export function OCRReviewForm({
    original, updated, handleSubmit
}: OCRReviewFormProps) {
    const formValues = useFormContext<OCRDataReviewFormValues>().watch();

    return (
        <form
            id="ocr-review-form"
            onSubmit={handleSubmit}
            noValidate
        >
            <CardTable headers={[
                '', 'Field', 'Original Value', 'Updated Value', 'Action', 'Override'
            ]}
            >
                {Object.entries(formValues).map(([ fieldName, values ]) => {
                    if (fieldName === 'borrowers') {
                        return null; // handled below
                    }

                    const { action } = values as OCRFieldValues;

                    const fieldNameTyped = fieldName as keyof Omit<LoanDetail, 'borrowers'>;
                    const fieldLabel = loanFieldLabels[fieldNameTyped];

                    const originalFieldValue: string | number | boolean | Partial<Address> | undefined = original?.[fieldName as keyof Omit<LoanDetail, 'assignee' | 'borrowers'>];
                    const updatedFieldValue: string | number | boolean | Partial<Address> | undefined = updated?.[fieldName as keyof Omit<LoanDetail, 'assignee' | 'borrowers'>];

                    const { fieldType, displayObject } = formFieldMappings[fieldName as keyof LoanDetail];

                    if (fieldType === 'enum' && !displayObject) {
                        throw new Error(`No display object found for field type: ${fieldType}`);
                    }

                    return (
                        <tr key={fieldName}>
                            <td className={styles.iconCell}>
                                {action === OCRFieldAction.APPROVE && (
                                    <Tooltip title="Approved">
                                        <CheckCircleOutline
                                            fontSize="small"
                                            color="success"
                                        />
                                    </Tooltip>
                                )}

                                {action === OCRFieldAction.REJECT && (
                                    <Tooltip title="Rejected">
                                        <Clear
                                            fontSize="small"
                                            color="error"
                                        />
                                    </Tooltip>
                                )}

                                {!action && (
                                    <Tooltip title="Pending Review">
                                        <HourglassBottom
                                            fontSize="small"
                                            color="primary"
                                        />
                                    </Tooltip>
                                )}

                                {action === OCRFieldAction.OVERRIDE && (
                                    <Tooltip title="Override">
                                        <EditNote
                                            fontSize="small"
                                            color="info"
                                        />
                                    </Tooltip>
                                )}
                            </td>

                            <td>
                                <Typography variant="body2">
                                    {fieldLabel}
                                </Typography>
                            </td>

                            <td>
                                <Typography
                                    variant="body2"
                                    className={styles.oldDiffValue}
                                >
                                    {loanFieldFormatter(fieldNameTyped, originalFieldValue!)}
                                </Typography>
                            </td>

                            <td>
                                <Typography
                                    variant="body2"
                                    className={styles.newDiffValue}
                                >
                                    {loanFieldFormatter(fieldNameTyped, updatedFieldValue!)}
                                </Typography>
                            </td>

                            <td>
                                <TextField<OCRDataReviewFormValues>
                                    select
                                    name={`${fieldNameTyped}.action`}
                                    size="small"
                                    className={clsx(styles.ocrFieldActionField, {
                                        [ocrDialogStyles.overrideText]: action === OCRFieldAction.OVERRIDE,
                                        [ocrDialogStyles.approvedText]: action === OCRFieldAction.APPROVE,
                                        [ocrDialogStyles.rejectedText]: action === OCRFieldAction.REJECT
                                    })}
                                    InputProps={{
                                        classes: {
                                            notchedOutline: clsx({
                                                [styles.borderApproved]: action === OCRFieldAction.APPROVE,
                                                [styles.borderRejected]: action === OCRFieldAction.REJECT,
                                                [styles.borderOverride]: action === OCRFieldAction.OVERRIDE
                                            })
                                        }
                                    }}
                                    required
                                    variant="outlined"
                                >
                                    {renderEnumOptions(ocrFieldActionDisplay)}
                                </TextField>
                            </td>

                            <td>
                                {action === OCRFieldAction.OVERRIDE ? (
                                    <>
                                        {fieldType === 'enum' && (
                                            <TextField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                size="small"
                                                select
                                                className={styles.overrideField}
                                                required
                                                variant="outlined"
                                            >
                                                {renderEnumOptions(displayObject!)}
                                            </TextField>
                                        )}

                                        {fieldType === 'boolean' && (
                                            <TextField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                size="small"
                                                select
                                                className={styles.overrideField}
                                                required
                                                variant="outlined"
                                            >
                                                {renderEnumOptions(booleanEnumDisplay)}
                                            </TextField>
                                        )}

                                        {fieldType === 'text' && (
                                            <TextField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                size="small"
                                                className={styles.overrideField}
                                                required
                                                variant="outlined"
                                            />
                                        )}

                                        {fieldType === 'currency' && (
                                            <CurrencyField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                size="small"
                                                className={styles.overrideField}
                                                required
                                                variant="outlined"
                                            />
                                        )}

                                        {fieldType === 'percentage' && (
                                            <PercentField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                variant="outlined"
                                                size="small"
                                                required
                                            />
                                        )}

                                        {fieldType === 'date' && (
                                            <DateField<OCRDataReviewFormValues>
                                                name={`${fieldNameTyped}.overwriteValue`}
                                                className={styles.overrideField}
                                                size="small"
                                                required
                                                variant="outlined"
                                            />
                                        )}

                                        {fieldType === 'address' && (
                                            <AddressFieldset
                                                fieldNames={{
                                                    street: `${fieldNameTyped}.overwriteValue.street`,
                                                    line2: `${fieldNameTyped}.overwriteValue.line2`,
                                                    city: `${fieldNameTyped}.overwriteValue.city`,
                                                    state: `${fieldNameTyped}.overwriteValue.state`,
                                                    zip: `${fieldNameTyped}.overwriteValue.zip`,
                                                    county: `${fieldNameTyped}.overwriteValue.county`
                                                }}
                                                className={styles.overrideField}
                                                required
                                            />
                                        )}
                                    </>
                                ) : <div className={styles.overrideField} />}
                            </td>
                        </tr>
                    );
                })}

                {!!updated?.borrowers?.length && (
                    <tr className={styles.unstyledRow}>
                        <td colSpan={6}>
                            <Typography
                                variant="subtitle1"
                                className={clsx(styles.sectionTitle, styles.indent)}
                            >
                                {updated?.borrowers?.length > 1 ? 'Borrowers' : 'Borrower'}
                            </Typography>
                        </td>
                    </tr>
                )}

                {formValues.borrowers?.map((borrower, borrowerIndex) => (
                    <Fragment key={borrower.id || borrowerIndex}>
                        {borrowerIndex > 0
                            && (
                                <tr className={styles.unstyledRow}>
                                    <td colSpan={6}>
                                        <Divider className={styles.divider} />
                                    </td>
                                </tr>
                            )}

                        <tr className={styles.unstyledRow}>
                            <td colSpan={6}>
                                <div className={clsx(styles.borrowerHeader, styles.indent)}>
                                    <BorrowerInfoTooltip
                                        borrower={getBorrowerFromFormValues(borrower, original?.borrowers || [])
                                            || getBorrowerFromFormValues(borrower, updated?.borrowers || [])}
                                        isNewBorrower={borrower.isNewBorrower}
                                    >
                                        <div className={styles.borrowerHeader}>
                                            {borrower.isNewBorrower ? (
                                                <PersonAdd
                                                    color="primary"
                                                    fontSize="small"
                                                    className={styles.borrowerIcon}
                                                />
                                            ) : (
                                                <Person
                                                    fontSize="small"
                                                    className={styles.borrowerIcon}
                                                />
                                            )}

                                            <Typography
                                                variant="body2"
                                                className={styles.borrowerName}
                                            >
                                                {borrower.isNewBorrower
                                                    ? 'New Borrower'
                                                    : `${getBorrowerFromFormValues(borrower, original?.borrowers || [])?.firstName || ''} ${getBorrowerFromFormValues(borrower, original?.borrowers || [])?.lastName || ''}`}
                                            </Typography>
                                        </div>
                                    </BorrowerInfoTooltip>
                                </div>
                            </td>
                        </tr>

                        {/* Map through borrower fields */}
                        {Object.entries(borrower).map(([ fieldName, values ]) => {
                            if (fieldName === 'isNewBorrower' || fieldName === 'id') {
                                return null; // Skip these fields
                            }

                            const { action } = values as OCRFieldValues;

                            const fieldNameTyped = fieldName as keyof Borrower;

                            const fieldLabel = borrowerFieldLabels[fieldNameTyped] || fieldNameTyped;

                            let originalBorrower: Partial<Borrower> | undefined;
                            if (!borrower.isNewBorrower) {
                                originalBorrower = original?.borrowers.find(
                                    b => isSameBorrower(b, getBorrowerFromFormValues(borrower, original?.borrowers!))
                                );
                            }
                            const updatedBorrower = updated?.borrowers?.find(
                                b => isSameBorrower(b, getBorrowerFromFormValues(borrower, updated?.borrowers!))
                            );

                            return (
                                <tr key={fieldName}>
                                    <td className={styles.iconCell}>
                                        {action === OCRFieldAction.APPROVE && (
                                            <Tooltip title="Approved">
                                                <CheckCircleOutline
                                                    fontSize="small"
                                                    color="success"
                                                />
                                            </Tooltip>
                                        )}

                                        {action === OCRFieldAction.REJECT && (
                                            <Tooltip title="Rejected">
                                                <Clear
                                                    fontSize="small"
                                                    color="error"
                                                />
                                            </Tooltip>
                                        )}

                                        {!action && (
                                            <Tooltip title="Pending Review">
                                                <HourglassBottom
                                                    fontSize="small"
                                                    color="primary"
                                                />
                                            </Tooltip>
                                        )}

                                        {action === OCRFieldAction.OVERRIDE && (
                                            <Tooltip title="Override">
                                                <EditNote
                                                    fontSize="small"
                                                    color="info"
                                                />
                                            </Tooltip>
                                        )}
                                    </td>

                                    <td>
                                        <div className={styles.borrowerFieldContainer}>
                                            <Typography
                                                variant="body2"
                                                className={styles.borrowerFieldName}
                                            >
                                                {fieldLabel}
                                            </Typography>
                                        </div>
                                    </td>

                                    <td>
                                        <Typography
                                            variant="body2"
                                            className={styles.oldDiffValue}
                                        >
                                            {borrower.isNewBorrower ? '' : borrowerFieldFormatter(fieldNameTyped, originalBorrower?.[fieldNameTyped] || '')}
                                        </Typography>
                                    </td>

                                    <td>
                                        <Typography
                                            variant="body2"
                                            className={styles.newDiffValue}
                                        >
                                            {borrowerFieldFormatter(fieldNameTyped, updatedBorrower?.[fieldNameTyped] || '')}
                                        </Typography>
                                    </td>

                                    <td>
                                        <TextField<OCRDataReviewFormValues>
                                            select
                                            name={`borrowers.${borrowerIndex}.${fieldNameTyped}.action` as FieldPath<OCRDataReviewFormValues>}
                                            size="small"
                                            className={clsx(styles.ocrFieldActionField, {
                                                [ocrDialogStyles.overrideText]: action === OCRFieldAction.OVERRIDE,
                                                [ocrDialogStyles.approvedText]: action === OCRFieldAction.APPROVE,
                                                [ocrDialogStyles.rejectedText]: action === OCRFieldAction.REJECT
                                            })}
                                            InputProps={{
                                                classes: {
                                                    notchedOutline: clsx({
                                                        [styles.borderApproved]: action === OCRFieldAction.APPROVE,
                                                        [styles.borderRejected]: action === OCRFieldAction.REJECT,
                                                        [styles.borderOverride]: action === OCRFieldAction.OVERRIDE
                                                    })
                                                }
                                            }}
                                            required
                                            variant="outlined"
                                        >
                                            {renderEnumOptions(ocrFieldActionDisplay)}
                                        </TextField>
                                    </td>

                                    <td>
                                        {action === OCRFieldAction.OVERRIDE ? (
                                            <>
                                                {(borrowerFormFieldMappings[fieldNameTyped]?.fieldType === 'enum'
                                                    && borrowerFormFieldMappings[fieldNameTyped]?.displayObject) && (
                                                    <TextField<OCRDataReviewFormValues>
                                                        name={`borrowers.${borrowerIndex}.${fieldNameTyped}.overwriteValue` as FieldPath<OCRDataReviewFormValues>}
                                                        size="small"
                                                        select
                                                        className={styles.overrideField}
                                                        required
                                                        variant="outlined"
                                                    >
                                                        {renderEnumOptions(
                                                            borrowerFormFieldMappings[fieldNameTyped]?.displayObject!
                                                        )}
                                                    </TextField>
                                                )}

                                                {borrowerFormFieldMappings[fieldNameTyped]?.fieldType === 'boolean' && (
                                                    <TextField<OCRDataReviewFormValues>
                                                        name={`borrowers.${borrowerIndex}.${fieldNameTyped}.overwriteValue` as FieldPath<OCRDataReviewFormValues>}
                                                        size="small"
                                                        select
                                                        className={styles.overrideField}
                                                        required
                                                        variant="outlined"
                                                    >
                                                        {renderEnumOptions(booleanEnumDisplay)}
                                                    </TextField>
                                                )}

                                                {borrowerFormFieldMappings[fieldNameTyped]?.fieldType === 'text' && (
                                                    <TextField<OCRDataReviewFormValues>
                                                        name={`borrowers.${borrowerIndex}.${fieldNameTyped}.overwriteValue` as FieldPath<OCRDataReviewFormValues>}
                                                        size="small"
                                                        className={styles.overrideField}
                                                        required
                                                        variant="outlined"
                                                    />
                                                )}

                                                {borrowerFormFieldMappings[fieldNameTyped]?.fieldType === 'ssn' && (
                                                    <TextField<OCRDataReviewFormValues>
                                                        name={`borrowers.${borrowerIndex}.${fieldNameTyped}.overwriteValue` as FieldPath<OCRDataReviewFormValues>}
                                                        size="small"
                                                        className={styles.overrideField}
                                                        required
                                                        variant="outlined"
                                                        inputProps={{
                                                            placeholder: '123-45-6789'
                                                        }}
                                                    />
                                                )}

                                                {borrowerFormFieldMappings[fieldNameTyped]?.fieldType === 'employment' && (
                                                    <div className={styles.employmentOverride}>
                                                        <Typography variant="caption">Employment overrides not supported</Typography>
                                                    </div>
                                                )}
                                            </>
                                        ) : <div className={styles.overrideField} />}
                                    </td>
                                </tr>
                            );
                        })}
                    </Fragment>
                ))}
            </CardTable>
        </form>
    );
}

export function ocrReviewFormIsIncomplete(formValues: OCRDataReviewFormValues): boolean {
    return Object.entries(formValues).some(([ fieldName, value ]) => {
        if (fieldName === 'borrowers') {
            return (value as BorrowerOCRFormValues[]).some(
                (borrower) => Object.entries(borrower).some(([ fieldName, value ]) => {
                    if (fieldName === 'isNewBorrower' || fieldName === 'id') {
                        return false; // we don't need to check this field
                    }

                    return !(value as OCRFieldValues).action;
                })
            );
        }

        return !(value as OCRFieldValues).action;
    });
}

function borrowerFieldFormatter(
    fieldName: keyof Borrower, value: string | number | boolean | Partial<Address> | Employment[]
): ReactNode {
    const { fieldType } = borrowerFormFieldMappings[fieldName];

    if (fieldType === 'boolean') {
        return booleanEnumDisplay[value ? BooleanEnum.TRUE : BooleanEnum.FALSE];
    }

    if (fieldType === 'ssn') {
        return <SSNDisplay ssn={value as string} />;
    }

    if (fieldType === 'employment') {
        const employmentValue = value as Employment[];
        return (
            <LabelGroup>
                {employmentValue.map((employment) => (
                    <Fragment key={JSON.stringify(employment)}>
                        <LabeledValue
                            label="Current employment indicator"
                            value={employment.currentEmploymentIndicator ? 'Yes' : 'No'}
                        />

                        <LabeledValue
                            label="Base pay amount"
                            value={formatCurrency(employment.basePayAmount)}
                        />
                    </Fragment>
                ))}
            </LabelGroup>
        );
    }

    // number, string
    return value as ReactNode;
};

const loanFieldFormatter = (fieldName: string, value: string | number | boolean | Partial<Address>): ReactNode => {
    const { fieldType } = formFieldMappings[fieldName as keyof LoanDetail];
    const { displayObject } = formFieldMappings[fieldName as keyof LoanDetail];

    if (fieldType === 'boolean') {
        return booleanEnumDisplay[value ? BooleanEnum.TRUE : BooleanEnum.FALSE];
    }

    if (fieldType === 'currency') {
        return formatCurrency(value as number);
    }

    if (fieldType === 'enum' && displayObject) {
        return displayObject[value as keyof typeof displayObject];
    }

    if (fieldType === 'date') {
        return formatDate(value as string);
    }

    if (fieldType === 'percentage') {
        return `${(value as number).toFixed(3)}%`;
    }

    if (fieldType === 'address') {
        const addressValue = value as Partial<Address>;
        return `${addressValue.street}, ${addressValue.city}, ${addressValue.state} ${addressValue.zip}`;
    }

    // text
    return value as ReactNode;
};

const loanFieldLabels: Record<keyof LoanDetail, string> = {
    loanNumber: 'Loan number',
    assignee: 'Assignee',
    loanStatus: 'Loan status',
    loanType: 'Loan type',
    loanAmount: 'Loan amount',
    interestRate: 'Interest rate',
    amortizationType: 'Amortization type',
    armMargin: 'Arm margin',
    armInitialCap: 'Arm initial cap',
    armSubsequentCap: 'Arm subsequent cap',
    armLifeCap: 'Arm life cap',
    escrowsFlag: 'Escrows flag',
    interestOnlyFlag: 'Interest only flag',
    loanTerm: 'Loan term',
    lockPeriod: 'Lock period',
    borrowers: 'Borrowers',
    propertyType: 'Property type',
    units: 'Number of units',
    occupancy: 'Occupancy type',
    purpose: 'Purpose',
    address: 'Property address',
    appraisedValue: 'Appraised value',
    salePrice: 'Sale price',
    customerName: 'Customer name',
    customerId: 'Customer ID',
    customerLoanNumber: 'Customer loan number',
    productCode: 'Product code',
    specialtyProgram: 'Specialty program',
    documentationType: 'Documentation type',
    subordinatedBalance: 'Subordinated balance',
    cashOutAmount: 'Cash out amount',
    limitedLiabilityCorp: 'Limited liability corp',
    commitmentType: 'Commitment type',
    commitmentIdentifier: 'Commitment identifier',
    mortgageInsFlag: 'Mortgage ins flag',
    mortgageInsCompany: 'Mortgage ins company',
    mortgageInsCoverage: 'Mortgage ins coverage',
    underwriteFlag: 'Underwrite flag',
    automatedUwSystem: 'AUS',
    automatedUwRecommendation: 'AUR',
    escrowReserveMonths: 'Escrow reserve months',
    coborrowerFlag: 'Co-borrower flag',
    borrower1QualifyingTotalIncome: 'Borrower 1 qualifying total income',
    borrower2QualifyingTotalIncome: 'Borrower 2 qualifying total income',
    firstTimeHomeBuyer: 'First time home buyer',
    comments: 'Comments',
    proposedHousingPayment: 'Proposed housing payment',
    otherPayments: 'Other payments',
    id: 'Loan ID',
    losLoanId: 'LOS loan ID',
    expirationDate: 'Expiration date',
    borrowerName: 'Borrower name',
    registrationType: 'Registration type',
    pricingResultId: 'Pricing result ID'
};

/**
 * Checks if two borrowers are the same based on their ID, SSN, or first and last name.
 */
export function isSameBorrower(borrower1: Partial<Borrower> = {}, borrower2: Borrower = {}): boolean {
    if (borrower1.id && borrower2.id && borrower1.id === borrower2.id) {
        return true;
    }

    if (borrower1.ssn && borrower2.ssn && borrower1.ssn === borrower2.ssn) {
        return true;
    }

    if (borrower1.firstName && borrower2.firstName && borrower1.lastName && borrower2.lastName
        && borrower1.firstName === borrower2.firstName && borrower1.lastName === borrower2.lastName) {
        return true;
    }

    return false;
}

/**
 * Gets the borrower object from the form values based on the ID.
 */
function getBorrowerFromFormValues(formValuesBorrower: BorrowerOCRFormValues, borrowers: Borrower[]) {
    // assert non-null because form values are derived from borrowers, there will always be a match
    return borrowers.find(b => b.id === formValuesBorrower.id)!;
}

type OCRFieldData = {
    fieldType: 'text' | 'enum' | 'boolean' | 'currency' | 'date' | 'percentage' | 'address' | 'employment' | 'ssn';
    displayObject?: { [key: string]: string };
};

const borrowerFormFieldMappings: Record<keyof Borrower, OCRFieldData> = {
    id: { fieldType: 'text' },
    firstName: { fieldType: 'text' },
    middleName: { fieldType: 'text' },
    lastName: { fieldType: 'text' },
    fico: { fieldType: 'text' },
    ssn: { fieldType: 'ssn' },
    email: { fieldType: 'text' },
    primaryWageEarner: { fieldType: 'boolean' },
    employment: { fieldType: 'employment' }
};

const formFieldMappings: Record<keyof LoanDetail, OCRFieldData> = {
    loanNumber: { fieldType: 'text' },
    assignee: { fieldType: 'text' },
    loanStatus: { fieldType: 'text' },
    loanType: {
        fieldType: 'enum',
        displayObject: loanTypeDisplay
    },
    loanAmount: { fieldType: 'currency' },
    interestRate: { fieldType: 'percentage' },
    amortizationType: {
        fieldType: 'enum',
        displayObject: amortizationTypeDisplay
    },
    armMargin: { fieldType: 'percentage' },
    armInitialCap: { fieldType: 'currency' },
    armSubsequentCap: { fieldType: 'currency' },
    armLifeCap: { fieldType: 'currency' },
    escrowsFlag: { fieldType: 'boolean' },
    interestOnlyFlag: { fieldType: 'boolean' },
    loanTerm: { fieldType: 'text' },
    lockPeriod: { fieldType: 'text' },
    borrowers: { fieldType: 'text' },
    propertyType: {
        fieldType: 'enum',
        displayObject: propertyTypeDisplay
    },
    units: {
        fieldType: 'enum',
        displayObject: numUnitsDisplay
    },
    occupancy: {
        fieldType: 'enum',
        displayObject: occupancyTypeDisplay
    },
    purpose: {
        fieldType: 'enum',
        displayObject: loanPurposeDisplay
    },
    address: { fieldType: 'address' },
    appraisedValue: { fieldType: 'currency' },
    salePrice: { fieldType: 'currency' },
    customerName: { fieldType: 'text' },
    customerId: { fieldType: 'text' },
    customerLoanNumber: { fieldType: 'text' },
    productCode: { fieldType: 'text' },
    specialtyProgram: {
        fieldType: 'enum',
        displayObject: specialtyProgramDisplay
    },
    documentationType: {
        fieldType: 'enum',
        displayObject: documentationTypeDisplay
    },
    subordinatedBalance: { fieldType: 'currency' },
    cashOutAmount: { fieldType: 'currency' },
    limitedLiabilityCorp: { fieldType: 'boolean' },
    commitmentType: {
        fieldType: 'enum',
        displayObject: commitmentTypeDisplay
    },
    commitmentIdentifier: { fieldType: 'text' },
    mortgageInsFlag: { fieldType: 'boolean' },
    mortgageInsCompany: { fieldType: 'text' },
    mortgageInsCoverage: { fieldType: 'percentage' },
    underwriteFlag: { fieldType: 'boolean' },
    automatedUwSystem: {
        fieldType: 'enum',
        displayObject: automatedUwSystemDisplay
    },
    automatedUwRecommendation: {
        fieldType: 'enum',
        displayObject: automatedUwRecommendationDisplay
    },
    escrowReserveMonths: { fieldType: 'text' },
    coborrowerFlag: { fieldType: 'boolean' },
    borrower1QualifyingTotalIncome: { fieldType: 'currency' },
    borrower2QualifyingTotalIncome: { fieldType: 'currency' },
    firstTimeHomeBuyer: { fieldType: 'boolean' },
    comments: { fieldType: 'text' },
    proposedHousingPayment: { fieldType: 'currency' },
    otherPayments: { fieldType: 'currency' },
    id: { fieldType: 'text' },
    losLoanId: { fieldType: 'text' },
    borrowerName: { fieldType: 'text' },
    expirationDate: { fieldType: 'date' },
    registrationType: { fieldType: 'text' },
    pricingResultId: { fieldType: 'text' }
};

const borrowerFieldLabels: Record<keyof Borrower, string> = {
    id: 'Borrower ID',
    firstName: 'First Name',
    middleName: 'Middle Name',
    lastName: 'Last Name',
    fico: 'FICO Score',
    ssn: 'Social Security Number',
    email: 'Email Address',
    primaryWageEarner: 'Primary Wage Earner',
    employment: 'Employment Information'
};
